import React, {
  FC,
  useEffect,
  useMemo,
  useRef,
} from "react";
import {
  ButtonBox,
  GridTable,
  InputDatePicker,
  LoadingBox,
  PrivilegesChecker,
  SelectBox,
  TextBox,
} from "../..";
import {
  ActionTypeEnum,
  DailyTransactionTypeEnum,
  HasFormIdModel,
  InputDatePickerTimeDisplayModeEnum,
  LookupItemModel,
  SearchDailyTransactionTypeEnum,
  TableActionColumnsStyleEnum,
  TransactionResponseModel,
} from "../../../models";
import { searchTransactions } from "../../../serviceBroker/transactionApiServiceBroker";
import {
  generateGuid,
  getLabelName,
  getPagePrivileges,
  isArabicCurrentLanguage,
} from "../../../utils";
import {
  useTransactionList,
  useTransactionLoading, useTransactionSearchRequest, useTransactionTotalRows,useTransactionRefreshListTrigger
  // useTransactionSearchRequest,
  // useTransactionTotalRows
} from "../common/hooks/transactions/useTransactionSelectors";
import { useTransactionActions } from "../common/hooks/transactions/useTransactionActions";
import { useTransactionComponentList } from "../common/hooks/transactions/useTransactionComponentList";
import {useTransactionHandlers} from "../common/hooks/transactions/useTransactionHandlers";
import {searchListInitialValues} from "../common/initialValues/initialValues";

interface TransactionListProps extends HasFormIdModel {
  transactionType: DailyTransactionTypeEnum;
  formId: number;
}
export const TransactionList: FC<TransactionListProps> = ({
  transactionType,
  formId,
}) => {
  //#region variables
  const isArabic = isArabicCurrentLanguage();
  const defaultPageSize = 50;
  //#endregion
  //#region state & hooks
  const list = useTransactionList();
  const searchRequest=useTransactionSearchRequest();
  const totalRows=useTransactionTotalRows();
  const loading = useTransactionLoading();
  const { handleSetList,handleSetLoading,handleSetSearchRequest,handleSetTotalRows } = useTransactionActions();
  const {handleAction}=useTransactionHandlers(transactionType,formId);
  const refreshListTrigger = useTransactionRefreshListTrigger();
  const { beneficiaryList,searchTypeList, isSalesComponent } =useTransactionComponentList(formId, transactionType);
  const customerAccountsSelectBoxMultiselectRef = useRef<any>();
  const typeSelectBoxMultiselectRef = useRef<any>();
  //#endregion
  //#region useEffect
  useEffect(() => {
    const fillData = async () => {
      handleSetLoading(true);
      try {
        await getTransactions();
      } catch (err) {
        handleSetList([]);
        handleSetTotalRows(0);
      } finally {
        handleSetLoading(false);
      }
    };
    fillData().then(() => {
    });
  }, [
    searchRequest.pageNumber,
    searchRequest.pageSize,
    searchRequest.searchGuid
  ]);
    useEffect(() => {
        // Only run if refreshListTrigger has a meaningful value (not default)
        if (refreshListTrigger && refreshListTrigger != "" ) {
            const fillData = async () => {
                handleSetLoading(true);
                try {
                    await getTransactions();
                } catch (err) {
                    handleSetList([]);
                    handleSetTotalRows(0);
                } finally {
                    handleSetLoading(false);
                }
            };
            fillData().then(() => {});
        }
    }, [refreshListTrigger]);
  //#endregion
  //#region functions
  const availableActions = [
    ActionTypeEnum.View,
    ActionTypeEnum.ModifyStart,
    ActionTypeEnum.DeleteStart,
    ActionTypeEnum.PrintPdf,
    //isTransactionApplicableForPayment() &&ActionTypeEnum.InitializeCustomerPayment,
    //isTransactionApplicableForRefund() &&   ActionTypeEnum.TransactionRefundIInitialization,
    //isTransactionApplicableForIssueSalesRequest() && ActionTypeEnum.IssueSaleTransactionFromSalePriceInitialization
  ].filter((action): action is ActionTypeEnum => Boolean(action));
  const actionResolver = (
    row: TransactionResponseModel,
    rowIndex: number
  ): ActionTypeEnum[] => {
    const availableActions: ActionTypeEnum[] = [];
    if (!row) return availableActions;
    const {
      isValidToUseAsTransaction,
      isValidForReturn,
      isValidToUseAsSource,
      isEnablePay
    } = row;
    if (isValidToUseAsTransaction || isValidToUseAsSource) {
      availableActions.push(
        ActionTypeEnum.IssueSaleTransactionFromSalePriceInitialization
      );
    }
    if (isValidForReturn) {
      availableActions.push(ActionTypeEnum.TransactionRefundIInitialization);
    }
    if (isEnablePay) {
      availableActions.push(ActionTypeEnum.InitializeCustomerPayment);
    }
    // Example: Add "View" for every odd row (for development or alternating UI behavior)
    // if (rowIndex % 2 !== 0) {
    //   availableActions.push(ActionTypeEnum.View);
    // }
    return availableActions;
  };
  const getTransactions = async () => {
    if (searchRequest.searchValue != null && searchRequest.searchValue != "") {
      searchRequest.itemName =
        searchRequest.searchType == SearchDailyTransactionTypeEnum.itemName
          ? searchRequest.searchValue
          : null;
      searchRequest.itemBarcode =
        searchRequest.searchType == SearchDailyTransactionTypeEnum.barCode
          ? searchRequest.searchValue
          : null;
      searchRequest.itemCode =
        searchRequest.searchType == SearchDailyTransactionTypeEnum.code
          ? searchRequest.searchValue
          : null;
      searchRequest.code =
        searchRequest.searchType ==
        SearchDailyTransactionTypeEnum.transactionCode
          ? searchRequest.searchValue
          : null;
    }
    handleSetSearchRequest({...searchRequest,transactionType:transactionType});
    const result = await searchTransactions({...searchRequest,transactionType:transactionType});
    handleSetList(result?.Result || []);
    handleSetTotalRows(result?.TotalRecoredCount || 0);
  };
  const onCurrentPageChange = async (pageNumber: number) => {
    if (pageNumber !== searchRequest.pageNumber) {
      const obj = { ...searchRequest };
      obj.pageNumber = pageNumber;
      handleSetSearchRequest(obj);
    }
  };
  const onPageSizeChange = async (pageSize: number) => {
    if (pageSize !== searchRequest.pageSize) {
      const obj = { ...searchRequest };
      obj.pageSize = pageSize;
      handleSetSearchRequest(obj);
    }
  };
  //#endregion
  //#region component
  const subHeaderComponent = useMemo(() => {
    return (
      <>
        <div className="row row-cols-1 row-cols-xxl-3 row-cols-xl-3 row-cols-lg-3 row-cols-md-2 row-cols-sm-1 g-md-4 g-sm-4 mb-1">
          <InputDatePicker
            selectedDate={searchRequest.fromDate}
            isClearable
            InputLabel={"from Date"}
            selectsEnd
            timeMode={InputDatePickerTimeDisplayModeEnum.TimeSelect}
            startDate={searchRequest.fromDate}
            className="form-control"
            // minDate={searchParams.fromDate}
            endDate={searchRequest.toDate}
            onChange={(date) => {
              handleSetSearchRequest({
                ...searchRequest,
                fromDate: date === null ? undefined : date,
              });
            }}
          />
          <InputDatePicker
            selectedDate={searchRequest.toDate}
            isClearable
            InputLabel={"to Date"}
            selectsEnd
            timeMode={InputDatePickerTimeDisplayModeEnum.TimeSelect}
            startDate={searchRequest.fromDate}
            className="form-control"
            minDate={searchRequest.fromDate}
            endDate={searchRequest.toDate}
            onChange={(date) => {
              handleSetSearchRequest({
                ...searchRequest,
                toDate: date === null ? undefined : date,
              });
            }}
          />

          <SelectBox
            labelName={
              isSalesComponent
                ? getLabelName("Customer")
                : getLabelName("supplier")
            }
            isSingleSelect={true}
            selectedValues={
              searchRequest.customerId !== null &&
              searchRequest.customerId !== undefined
                ? [searchRequest.customerId.toString()]
                : null
            }
            source={beneficiaryList}
            onStatusChange={(e: LookupItemModel) => {
              handleSetSearchRequest({
                ...searchRequest,
                customerId:
                  e === null || e.value === null ? null : Number(e.value),
              });
            }}
            multiselectRef={customerAccountsSelectBoxMultiselectRef}
          />
          <SelectBox
            labelName={getLabelName("Type")}
            isSingleSelect={true}
            selectedValues={[searchRequest.searchType.toString()] ?? null}
            source={searchTypeList}
            onStatusChange={(e: LookupItemModel) => {
              handleSetSearchRequest({
                ...searchRequest,
                searchType:e?.value !=null? Number(e.value):SearchDailyTransactionTypeEnum.itemName,
              });
            }}
            multiselectRef={typeSelectBoxMultiselectRef}
          />
          <TextBox
            labelName={getLabelName("Value")}
            inputName={"filterTextBox"}
            inputValue={searchRequest.searchValue}
            onChange={(e: any) => {
              handleSetSearchRequest({
                ...searchRequest,
                searchValue: e.target.value,
              });
            }}
          />
          <div className="col-md-4 d-flex justify-content-end">
            <PrivilegesChecker formId={formId} action="EnableSearch">
              <ButtonBox
                iconType="account-search"
                className="btn btn-primary btn-md mx-2 mb-2"
                onClick={() => {
                  handleSetSearchRequest({
                    ...searchRequest,
                    searchGuid: generateGuid(),
                  });
                }}
              >
                {getLabelName("Search")}
              </ButtonBox>
            </PrivilegesChecker>

            <ButtonBox
              iconType="receipt"
              variant="danger"
              className="btn btn-danger btn-md mb-2"
              onClick={() => {
                customerAccountsSelectBoxMultiselectRef.current.clearValue();
                typeSelectBoxMultiselectRef.current.clearValue();
                handleSetSearchRequest({
                  ...searchListInitialValues(transactionType),
                  searchGuid: generateGuid(),
                });
              }}
            >
              {getLabelName("Cancel")}
            </ButtonBox>
          </div>
        </div>
      </>
    );
  }, [searchRequest, beneficiaryList,  searchTypeList]);
  //#endregion
  //#region html
  return (
    <>
      {loading && <LoadingBox />}
      {getPagePrivileges().EnableSearch && (
        <PrivilegesChecker formId={formId} action="EnableSearch">
          {subHeaderComponent}
        </PrivilegesChecker>
      )}
      <GridTable
        data={list}
        totalRows={totalRows}
        currentPage={searchRequest.pageNumber || 1}
        pageSize={searchRequest.pageSize || defaultPageSize}
        onCurrentPageChange={onCurrentPageChange}
        onPageSizeChange={onPageSizeChange}
        paginationType="server"
        columnsProps={{
          actions: availableActions, //  [],
          onActionEvent:handleAction,
          isArabic,
        }}
        externalLoading={loading}
        formId={formId}
        style={TableActionColumnsStyleEnum.Dropdown}
        actionResolver={actionResolver}
        useRowBasedActions={true}
        // subHeaderComponent={getPagePrivileges().search && subHeaderComponent}
      />
    </>
  );
  //#endregion
};
